import { useState, type ReactElement } from 'react';
import { PublicationStatus } from '@gr/core/distribution/publishing/dto';
import { zonedToUtc } from '@gr/core/time/zoned-time';
import { BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import { Link, useServices, useT } from '../../../app/di';
import { formatDateTime, fromLocalInputValue } from '../../../shared/format';
import { useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Card } from '../../../ui-kit/Card/Card';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { SplitLayout } from '../../../ui-kit/SplitLayout/SplitLayout';
import { IssueList } from '../channels';
import { PublicationStatusBadge } from '../scheduling';
import { useBlog } from '../../accounts/workspaces';
import { CancelPublicationForm } from './CancelPublicationForm/CancelPublicationForm';
import { MarkPublishedForm } from './MarkPublishedForm/MarkPublishedForm';
import { PackageCaption } from './PackageCaption/PackageCaption';
import { PackageMedia } from './PackageMedia/PackageMedia';

// PUB-UC-2, PUB-UC-3, PUB-UC-4, PUB-UC-5
export function PublicationPage({ publicationId }: { publicationId: string }): ReactElement {
  const { api } = useServices();
  const { t, te, locale } = useT();
  const { blog, can } = useBlog();
  const pkg = useResource(() => api.publishing.package(blog.id, publicationId), [api, blog.id, publicationId]);
  const [copied, setCopied] = useState<boolean | null>(null);
  const [shareFailed, setShareFailed] = useState(false);

  if (pkg.loading && !pkg.data) return <Loading label={t('common.loading')} />;
  if (pkg.error || !pkg.data) return <Alert tone="danger">{te(pkg.error)}</Alert>;
  const data = pkg.data;
  const publication = data.publication;
  const open = publication.status === PublicationStatus.Due || publication.status === PublicationStatus.Planned;

  const copyCaption = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(data.caption);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  // Web Share API с файлами — на телефоне сохраняет фото в галерею одной кнопкой.
  const share = async (): Promise<void> => {
    setShareFailed(false);
    const files = await api.publishing.packageFiles(data.media);
    if (!files.success || !files.data || !navigator.canShare?.({ files: files.data })) {
      setShareFailed(true);
      return;
    }
    try {
      await navigator.share({ files: files.data, text: data.caption });
    } catch (error) {
      if ((error as Error).name !== 'AbortError') setShareFailed(true);
    }
  };

  return (
    <div className="u-stack-lg">
      <PageHeader
        title={publication.postTitle}
        subtitle={
          <span className="u-row">
            <Link to="due" params={{ blogId: blog.id }}>
              {t('publishing.package.back')}
            </Link>
            <Link to="post" params={{ blogId: blog.id, postId: publication.postId }}>
              {t('publishing.package.openPost')}
            </Link>
          </span>
        }
        actions={
          <span className="u-row">
            <strong>{publication.channelName}</strong>
            <span>{formatDateTime(publication.plannedAt, locale, blog.timezone)}</span>
            <PublicationStatusBadge status={publication.status} />
          </span>
        }
      />
      {data.issues.length > 0 ? (
        <Alert tone="warning" title={t('publishing.package.issues')}>
          <IssueList issues={data.issues} />
        </Alert>
      ) : null}
      <SplitLayout>
        <>
          <PackageCaption caption={data.caption} droppedTags={data.droppedTags} channelUrl={data.channelUrl} copied={copied} onCopy={() => void copyCaption()} />
          <PackageMedia media={data.media} zipUrl={api.publishing.zipUrl(blog.id, publicationId)} shareFailed={shareFailed} onShare={() => void share()} />
        </>
        {open && can(BlogPermission.Publish) ? (
          <>
            <Card title={t('publishing.package.markTitle')}>
              <MarkPublishedForm
                early={publication.status === PublicationStatus.Planned}
                onSubmit={async (values) => {
                  const result = await api.publishing.markPublished(blog.id, publicationId, {
                    url: values.url || undefined,
                    publishedAt: values.publishedAt ? (fromLocalInputValue(values.publishedAt, blog.timezone, zonedToUtc) ?? undefined) : undefined,
                  });
                  if (result.success) await pkg.reload();
                  return result;
                }}
              />
            </Card>
            {publication.status === PublicationStatus.Due ? (
              <Card title={t('publishing.package.cancelTitle')}>
                <CancelPublicationForm
                  onSubmit={async (note) => {
                    const result = await api.publishing.cancel(blog.id, publicationId, note || undefined);
                    if (result.success) await pkg.reload();
                    return result;
                  }}
                />
              </Card>
            ) : null}
          </>
        ) : (
          <Card>
            <Alert tone={publication.status === PublicationStatus.Published ? 'success' : 'info'}>
              {t('publishing.package.done', { status: t(`scheduling.status.${publication.status}`) })}
              {publication.externalUrl ? (
                <>
                  {' '}
                  <a href={publication.externalUrl} target="_blank" rel="noreferrer noopener">
                    {publication.externalUrl}
                  </a>
                </>
              ) : null}
            </Alert>
          </Card>
        )}
      </SplitLayout>
    </div>
  );
}
